/**
 * CompletedTransactionCard - iter 971g compact redesign
 *
 * Symmetric to PendingTransactionCard but for the Completed tab.
 * Provides parity with TransactionTable (completed mode):
 * Undo · View doc / View details · Split · Change GL · Tax toggles ·
 * Unlink · Multi-select · Reversed state.
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowDownLeft, ArrowUpRight, Undo2, Scissors, FileText, Eye,
  MoreHorizontal, Link2, Unlink, X, Sparkles, Loader2, BookOpen, ChevronDown,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { TaxRateSelect } from './TaxRateSelect';
import { CounterpartyTaxFix } from './CounterpartyTaxFix';
import { FeeSplitBadge } from './StatementIntegrityChips';
import { pickCategoryDisplay } from './bankFeedDisplay';
import { GlAccountInlineSelect } from './GlAccountInlineSelect';
import { InlineTextEditor } from './InlineTextEditor';
import { ProjectSelect } from '../projects/ProjectSelect';
import { BftAttachmentsButton } from './BftAttachmentsButton';
import { ChannelChip } from './ChannelChip';
import { EditableFieldBox } from './EditableFieldBox';
import { LinkedDocChip } from './LinkedDocChip';
import { User, MessageSquare } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { rowCurrencyOf, RowFxEquivalent } from './RowFxEquivalent';

const isBankSideName = (n) => {
  const s = String(n || '').toLowerCase().trim();
  return /^bank account\b/.test(s) || /^bank\s*[-–-]/.test(s)
    || /^cash\b/.test(s) || /^petty cash\b/.test(s) || s === 'cash and cash equivalents';
};

// Plain-text bank label: "<Bank Name> ***1234"
const bankPlainText = (bankAccounts, bankAccountId) => {
  if (!bankAccountId || !Array.isArray(bankAccounts)) return '';
  const acct = bankAccounts.find((b) => b.id === bankAccountId);
  if (!acct) return '';
  const name = acct.bank_name || acct.account_name || 'Bank';
  const last4 = acct.account_number ? `***${String(acct.account_number).slice(-4)}` : '';
  return last4 ? `${name} ${last4}` : name;
};


const CategoryPill = ({ item, glAccounts, onChange, saving, disabled, pendingEdit, onSaveRow, savingRow }) => {
  const cat = pickCategoryDisplay(item) || {};
  const baseLabel = (cat.name && cat.name !== 'Uncategorised' && !isBankSideName(cat.name))
    ? cat.name
    : (item.ai_gl_credit_name && !isBankSideName(item.ai_gl_credit_name) ? item.ai_gl_credit_name
      : (item.ai_gl_debit_name && !isBankSideName(item.ai_gl_debit_name) ? item.ai_gl_debit_name : '-'));
  // Jul 16 fix - reflect staged edits + offer inline Save (table parity)
  const label = pendingEdit ? pendingEdit.glName : baseLabel;
  const code = pendingEdit ? pendingEdit.glCode : (cat.code || item.ai_gl_debit || item.ai_gl_credit);
  if (disabled) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`completed-cat-readonly-${item.id}`}>
        <BookOpen className="w-3 h-3" />
        <span className="truncate max-w-[220px]">{label}</span>
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-0.5 min-w-0" data-testid={`completed-card-category-${item.id}`}>
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">Category:</span>
        <GlAccountInlineSelect
          value={code}
          valueName={label}
          accounts={glAccounts || []}
          onChange={onChange}
          saving={saving}
          direction={item.direction}
          edited={!!pendingEdit}
          testId={`completed-gl-select-${item.id}`}
        />
      </div>
      {pendingEdit ? (
        <div className="flex items-center gap-1.5 pl-1">
          <span className="text-[9px] font-medium text-amber-700 uppercase tracking-wide" data-testid={`completed-gl-pending-${item.id}`}>
            • Unsaved
          </span>
          {onSaveRow && (
            <Button
              size="sm"
              variant="outline"
              className="h-5 px-1.5 text-[10px] font-medium border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => onSaveRow(item)}
              disabled={savingRow}
              data-testid={`completed-gl-save-row-${item.id}`}
            >
              {savingRow ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </Button>
          )}
        </div>
      ) : (
        <span
          className="hidden sm:inline text-[10px] text-muted-foreground italic pl-1"
          data-testid={`completed-category-hint-${item.id}`}
        >
          Click to change category
        </span>
      )}
    </div>
  );
};

export const CompletedTransactionCard = React.memo(({
  item,
  index = 0,
  isSelected = false,
  isNew = false,
  onSelect,
  onUndo,
  onSplit,
  onViewDocument,
  onViewDetails,
  onChangeGl,
  onChangeProject,
  onUnlinkDocument,
  onOpenInBooks = null,       // iter1411: card-view parity — In Books drill
  onOpenLinkDialog = null,    // iter1411: card-view parity — Link to existing document
  formatAmount,
  getBankLabel,
  getStatusBadge,
  glAccounts = [],
  bankAccounts = [],
  taxToggle = null,   // { taxStatus, onFetchStatus, onToggleVat, onToggleWht, isToggling }
  // Feb 2026 - flat props (see PendingTransactionCard for rationale).
  taxCtx = null,
  taxStatusForItem = undefined,
  onFetchTaxStatus = null,
  onToggleVat: onToggleVatProp = null,
  onToggleWht: onToggleWhtProp = null,
  isTogglingTax = false,
  onTaxSaved = null,
  pendingGlEdits = null,   // Jul 16: staged GL edits Map (parity w/ pending card)
  onSaveRow = null,        // Jul 16: per-row Save Changes handler
  savingRow = false,       // Jul 16: row-level saving state
  onSaveCounterparty = null, // Jul 17: contact name fix
  onSaveDescription = null,  // iter1102: description parity with Pending card
  onRefresh = null,          // iter 998: refresh after fee-split revert
  baseCurrency = null,       // business base currency (for the FX equivalent line)
}) => {
  const isInflow = item.direction === 'inflow';
  // Every row renders in ITS bank account's currency, never the page-level selection.
  const rowCcy = rowCurrencyOf(item, bankAccounts, baseCurrency);
  const isReversed = item.is_reversed || item.status === 'reversed';
  const confidence = item.ai_confidence || item.confidence || 0;
  const [savingGl, setSavingGl] = useState(false);

  // Feb 2026 - collapse legacy `taxToggle` OR new flat props (memoised).
  const resolvedTaxToggle = React.useMemo(() => {
    if (taxToggle) return taxToggle;
    if (!taxCtx && !onFetchTaxStatus && !onToggleVatProp && !onToggleWhtProp) return null;
    return {
      taxCtx,
      taxStatus: taxStatusForItem,
      onFetchStatus: onFetchTaxStatus,
      onToggleVat: onToggleVatProp,
      onToggleWht: onToggleWhtProp,
      isToggling: isTogglingTax,
      onSaved: onTaxSaved,
    };
  }, [taxToggle, taxCtx, taxStatusForItem, onFetchTaxStatus, onToggleVatProp, onToggleWhtProp, isTogglingTax, onTaxSaved]);

  const handleGlChange = async (account) => {
    if (!onChangeGl) return;
    setSavingGl(true);
    try { await onChangeGl(item, account); } finally { setSavingGl(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: (index || 0) * 0.02 }}
    >
      <Card
        className={`hover:shadow-sm transition-all overflow-hidden ${
          isSelected ? 'ring-2 ring-[#108981] ring-offset-1' : ''
        } ${isReversed ? 'opacity-60 border-l-4 border-l-purple-400' : ''}`}
        data-testid={`completed-item-${item.id}`}
      >
        <CardContent className="p-2.5 sm:p-3 space-y-1.5 sm:space-y-2">
          {/* Row 1 — Money line (never wraps on mobile). Status + NEW badge
              only. SYNC / source / transfer / fee-split / inventory chips
              are demoted to the meta strip below. */}
          <div className="flex items-center gap-2 min-w-0">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onSelect?.(item.id)}
              data-testid={`completed-checkbox-${item.id}`}
            />
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isInflow ? 'bg-green-100' : 'bg-red-100'}`}>
              {isInflow
                ? <ArrowDownLeft className="w-3.5 h-3.5 text-green-600" />
                : <ArrowUpRight className="w-3.5 h-3.5 text-red-600" />}
            </div>
            <span className={`text-sm sm:text-base font-semibold whitespace-nowrap ${
              isReversed ? 'line-through text-muted-foreground'
                : isInflow ? 'text-green-600' : 'text-red-600'}`}>
              {isInflow ? '+' : '-'}{rowCcy ? formatCurrency(Math.abs(item.amount || 0), rowCcy) : (formatAmount?.(item.amount) || item.amount?.toLocaleString())}
            </span>
            <RowFxEquivalent tx={item} rowCurrency={rowCcy} baseCurrency={baseCurrency} className="hidden sm:inline" />
            <span className="text-[11px] sm:text-xs text-muted-foreground whitespace-nowrap">· {item.date}</span>
            {(() => {
              const bt = bankPlainText(bankAccounts, item.bank_account_id);
              if (!bt) return null;
              return (
                <span
                  className="hidden sm:inline text-[11px] sm:text-xs text-muted-foreground whitespace-nowrap truncate max-w-[180px]"
                  title={bt}
                  data-testid={`completed-card-bank-label-${item.id}`}
                >
                  · {bt}
                </span>
              );
            })()}
            <div className="ml-auto flex items-center gap-1 shrink-0">
              {isNew && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500 text-white leading-none">NEW</span>
              )}
              {getStatusBadge?.(isReversed ? 'reversed' : item.status, confidence)}
            </div>
          </div>
          {/* Bank label on its own row on mobile (never wraps into badge column) */}
          {(() => {
            const bt = bankPlainText(bankAccounts, item.bank_account_id);
            if (!bt) return null;
            return (
              <div
                className="sm:hidden text-[11px] text-muted-foreground truncate pl-8 -mt-1 flex items-center gap-2"
                title={bt}
                data-testid={`completed-card-bank-label-mobile-${item.id}`}
              >
                <span className="truncate">{bt}</span>
                <RowFxEquivalent tx={item} rowCurrency={rowCcy} baseCurrency={baseCurrency} />
              </div>
            );
          })()}

          {/* Row 2 — Counterparty + Description in bordered edit boxes.
              Description is now editable on Completed too (parity with
              Pending; previously read-only). Reversed rows are read-only. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 sm:pl-8">
            <EditableFieldBox
              icon={User}
              value={item.ai_counterparty || item.counterparty || ''}
              placeholder="Add contact name…"
              label="contact name"
              disabled={isReversed}
              onSave={(next) => onSaveCounterparty?.(item, next)}
              testIdPrefix={`completed-contact-${item.id}`}
            />
            <EditableFieldBox
              icon={MessageSquare}
              value={item.description || ''}
              placeholder={isReversed ? '' : 'Add a description…'}
              label="description"
              multiline
              disabled={isReversed || !onSaveDescription}
              onSave={(next) => onSaveDescription?.(item, next)}
              testIdPrefix={`completed-desc-${item.id}`}
            />
          </div>
          {isReversed && (
            <div className="sm:pl-8 text-[10px] text-purple-600 truncate">
              Reversed{item.reversal_reason ? `: ${item.reversal_reason.replace(/_/g, ' ')}` : ''}
            </div>
          )}

          {/* Row 3 - category · tax · linked doc */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap min-w-0 sm:pl-8">
            <CategoryPill
              item={item}
              glAccounts={glAccounts}
              onChange={handleGlChange}
              saving={savingGl}
              disabled={isReversed}
              pendingEdit={pendingGlEdits?.get?.(item.id) || null}
              onSaveRow={onSaveRow}
              savingRow={savingRow}
            />
            {!isReversed && (
              <ProjectSelect
                compact
                value={item.project_id || null}
                onChange={(pid) => onChangeProject?.(item, pid)}
                createdFrom="bank_feed"
                autoMatched={!!item.project_auto_matched}
              />
            )}
            {resolvedTaxToggle && !isReversed && (
              <>
                {resolvedTaxToggle.taxCtx?.show_vat && (
                  <TaxRateSelect
                    kind="vat"
                    transaction={item}
                    taxCtx={resolvedTaxToggle.taxCtx}
                    taxStatus={resolvedTaxToggle.taxStatus}
                    onFetchStatus={resolvedTaxToggle.onFetchStatus}
                    onSaved={resolvedTaxToggle.onSaved}
                  />
                )}
                {resolvedTaxToggle.taxCtx?.show_wht && (
                  <TaxRateSelect
                    kind="wht"
                    transaction={item}
                    taxCtx={resolvedTaxToggle.taxCtx}
                    taxStatus={resolvedTaxToggle.taxStatus}
                    onFetchStatus={resolvedTaxToggle.onFetchStatus}
                    onSaved={resolvedTaxToggle.onSaved}
                  />
                )}
                <CounterpartyTaxFix item={item} taxStatus={resolvedTaxToggle.taxStatus} compact
                  onChanged={(txId, fresh) => { if (resolvedTaxToggle.onFetchStatus) resolvedTaxToggle.onFetchStatus(txId, true); if (resolvedTaxToggle.onSaved) resolvedTaxToggle.onSaved(txId, fresh); }} />
              </>
            )}
            <LinkedDocChip item={item} onUnlink={isReversed ? null : onUnlinkDocument} testIdPrefix="completed-card" />
            <BftAttachmentsButton bftId={item.id} compact />
          </div>

          {/* Meta strip — demoted low-emphasis metadata. Was previously in
              Row 1 as pills and caused staircase wrapping on mobile.
              Single line, muted, groups sync channel + auto-flags. */}
          {(item.channel || item.source || item.inventory_attachment || item.transfer_pair?.confirmed) && (
            <div
              className="sm:pl-8 flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap"
              data-testid={`completed-meta-strip-${item.id}`}
            >
              <ChannelChip item={item} size="xs" />
              <FeeSplitBadge tx={item} onDone={onRefresh} />
              {item.transfer_pair?.confirmed && (
                <span className="inline-flex items-center gap-0.5 text-indigo-700">
                  <BookOpen className="w-2.5 h-2.5" /> Own-account transfer
                </span>
              )}
              {item.inventory_attachment && (
                <span
                  className="inline-flex items-center gap-0.5 text-teal-700"
                  title={`Attached to inventory purchase as "${item.inventory_attachment.cost_name || 'landed cost'}"`}
                >
                  <BookOpen className="w-2.5 h-2.5" /> In stock cost
                </span>
              )}
            </div>
          )}

          {/* Row 4 - actions */}
          {!isReversed && (
            <div className="flex items-center gap-1 sm:pl-8 pt-0.5 flex-wrap">
              {onViewDetails && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px] sm:text-xs gap-1"
                  onClick={() => onViewDetails(item)}
                  data-testid={`completed-view-details-${item.id}`}
                >
                  <Eye className="w-3.5 h-3.5" /><span>View</span>
                </Button>
              )}

              {onUndo && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px] sm:text-xs gap-1 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                  onClick={() => onUndo(item)}
                  data-testid={`completed-undo-${item.id}`}
                  title="Remove from ledgers and move back to Review Needed"
                >
                  <Undo2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Undo Classification</span>
                  <span className="sm:hidden">Undo</span>
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[11px] sm:text-xs gap-1 ml-auto border-slate-300 text-slate-700 hover:bg-slate-100 font-medium"
                    aria-label="More actions"
                    title="More actions: Split, Re-link, Unlink, Raw details"
                    data-testid={`completed-more-${item.id}`}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                    <span>More</span>
                    <ChevronDown className="w-3 h-3 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {onSplit && (
                    <DropdownMenuItem onSelect={() => onSplit(item)} className="gap-2 text-xs"
                      data-testid={`completed-split-${item.id}`}>
                      <Scissors className="w-3.5 h-3.5 text-amber-600" />Split transaction
                    </DropdownMenuItem>
                  )}
                  {/* iter1411 · Card-view parity: replace linkage.
                      Opens SmartLinkDocumentModal in relink mode which
                      atomically hard-deletes the prior journal + doc
                      linkage and posts a fresh composite settlement.
                      Gate on matched_document_id (not matched_document_ref)
                      because matched rows carry the id with ref=null. */}
                  {onOpenLinkDialog && (item.matched_document_id || item.created_document_id) && (
                    <DropdownMenuItem
                      onSelect={() => onOpenLinkDialog(item, { mode: 'relink' })}
                      className="gap-2 text-xs text-amber-700 focus:text-amber-800"
                      data-testid={`completed-relink-${item.id}`}
                    >
                      <Link2 className="w-3.5 h-3.5" />
                      Link to existing document…
                    </DropdownMenuItem>
                  )}
                  {onUnlinkDocument && (item.matched_document_ref || item.matched_document_id) && (
                    <DropdownMenuItem onSelect={() => onUnlinkDocument(item)} className="gap-2 text-xs">
                      <Unlink className="w-3.5 h-3.5 text-red-600" />Unlink document
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {onViewDetails && (
                    <DropdownMenuItem onSelect={() => onViewDetails(item)} className="gap-2 text-xs">
                      <Eye className="w-3.5 h-3.5" />View raw details
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
});
CompletedTransactionCard.displayName = 'CompletedTransactionCard';

export default CompletedTransactionCard;
